/** /users/{system|staff|guests} — kind-scoped user lists sharing one grid. */
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LockResetIcon from '@mui/icons-material/LockReset';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { getGridStringOperators, type GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as groupsApi from '@/api/groups';
import type { BookingTag } from '@/api/bookings';
import * as usersApi from '@/api/users';
import type { ConsoleUserListItem, UserKind } from '@/api/users';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { deleteBatchAction, type BatchActionDef } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { entityExportBatchAction } from '@/features/exports/EntityExportDialog';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatRelativeTime } from '@/utils/format';

import { PromoteToStaffDialog } from './PromoteToStaffDialog';
import { UserInviteDialog } from './UserInviteDialog';

const boolStr = (v: string) => (v === 'true' || v === 'false' ? v === 'true' : undefined);

// Text columns share the backend `*_contains` params — offer only 'contains'.
const containsOnly = getGridStringOperators().filter((op) => op.value === 'contains');

/** Header-filter columns (contains) + toolbar-only tenant / status / deleted.
 *  Header row and toolbar share these URL params. */
const FILTERS: FilterDef[] = [
  { param: 'email_contains', column: { field: 'email', operator: 'contains' } },
  { param: 'first_name_contains', column: { field: 'first_name', operator: 'contains' } },
  { param: 'last_name_contains', column: { field: 'last_name', operator: 'contains' } },
  { param: 'phone_contains', column: { field: 'phone', operator: 'contains' } },
  { param: 'is_active', toApi: boolStr },
  { param: 'is_staff', toApi: boolStr },
  { param: 'group' },
  { param: 'has_mfa', toApi: boolStr },
  { param: 'tenant', apiParam: 'tour_operator', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'deleted', apiParam: 'include_deleted', toApi: boolStr },
  // Account-level tag filter (CP-8095): a tag id, or a case-insensitive
  // substring of the tag name. Toolbar-only — set by clicking a Tags chip.
  { param: 'tag' },
];

/** Page copy per user kind. Exported because `routes.tsx` reuses the titles
 *  for the document title — one string, not two that can drift. */
export const KIND_COPY: Record<UserKind, { title: string; subtitle: string }> = {
  system: { title: 'System users', subtitle: 'Internal staff with access to the Ops Console.' },
  staff: { title: 'Staff', subtitle: 'Tour-operator staff (dashboard users).' },
  guest: { title: 'Guests', subtitle: 'Travellers using the mobile app and booking portals.' },
};

/** Presets store the users filter state as `{params: {...}}` over the same
 *  URL params useGridFilters owns. */
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

const KIND_GRID = { system: 'users_system', staff: 'users_staff', guest: 'users_guest' } as const;

/** What an MFA reset signs out: the sessions of the user's one sign-in scope (dashboard
 *  password MFA plan §3.8) — the dashboard for staff, the console for system users. */
const MFA_RESET_SESSIONS: Record<UserKind, string> = {
  system: 'console sessions',
  staff: 'dashboard sessions',
  guest: 'sessions',
};

/** Supplementary columns hidden by default (opt-in via the columns menu). */
const USERS_HIDDEN = ['login', 'email_verified', 'phone_verified', 'is_employee', 'date_joined'];

/** Column field order per kind (mirrors the columns memo below — keep in sync). */
function defaultFieldOrder(kind: UserKind, showTenant: boolean): string[] {
  return [
    'email',
    'login',
    'email_verified',
    'first_name',
    'last_name',
    ...(showTenant ? ['tour_operator'] : []),
    ...(kind !== 'system' ? ['phone', 'phone_verified'] : []),
    'is_active',
    'is_employee',
    ...(kind !== 'guest' ? ['mfa_enrolled'] : []),
    ...(kind === 'system' ? ['groups', 'pending_invite_count'] : []),
    'active_tokens_count',
    'tags',
    'last_login',
    'date_joined',
  ];
}

interface Props {
  kind: UserKind;
  /** When set (embedded mode, e.g. the tour-operator Staff tab): hardcode the
   *  tenant filter to this id, and hide the page header, the Tenant column,
   *  and the Tenant toolbar filter. */
  fixedTenantId?: number;
}

export function UsersListPage({ kind, fixedTenantId }: Props) {
  const { hasPerm } = usePermissions();
  const embedded = fixedTenantId !== undefined;
  const showTenant = kind !== 'system' && !embedded;

  const [inviteOpen, setInviteOpen] = useState(false);
  useCreateIntent(() => setInviteOpen(true));

  const presets = useGridPresets({
    grid: KIND_GRID[kind],
    defaultOrder: useMemo(() => defaultFieldOrder(kind, showTenant), [kind, showTenant]),
    defaultHidden: USERS_HIDDEN,
    defaultOrdering: kind === 'system' ? '-date_joined' : '-id',
    filters: PRESET_FILTERS,
    enabled: !embedded, // the Staff tab reuses this page without preset/URL state
  });

  // -- URL-backed filters shared by the header row and the toolbar ----------
  // (project grid rule; see useGridFilters). The text columns get header
  // filters; tenant / status / include-deleted are toolbar-only. A loaded
  // preset supplies filter values through the preset layer.
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // Clicking a tag chip toggles the `tag` filter to that tag's id (ids are exact,
  // so two same-named tags stay distinguishable).
  const activeTagParam = getParam('tag') ?? '';
  const isTagActive = useCallback(
    (tag: BookingTag) => activeTagParam === String(tag.id),
    [activeTagParam],
  );
  const onToggleTag = useCallback(
    (tag: BookingTag) => setParam('tag', activeTagParam === String(tag.id) ? null : String(tag.id)),
    [activeTagParam, setParam],
  );

  // The operators the effective tenant filter names (URL > preset > scope); the
  // picker resolves their names itself.
  const tenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const activeRaw = getParam('is_active');
  const status: 'all' | 'active' | 'inactive' =
    activeRaw === 'true' ? 'active' : activeRaw === 'false' ? 'inactive' : 'all';
  const includeDeleted = getParam('deleted') === 'true';

  // Backend-supported facets. `is_staff` applies to any non-guest population;
  // `group` (console/django groups) is system-only in the UI, and so is `has_mfa`
  // (a Python-side membership scan, safe only for a small population) — except on a
  // tenant's Staff tab, one tenant's staff, which its Sign-in security tab links to
  // filtered `has_mfa=false`.
  const isStaffRaw = getParam('is_staff');
  const isStaff: 'all' | 'true' | 'false' =
    isStaffRaw === 'true' || isStaffRaw === 'false' ? isStaffRaw : 'all';
  const groupParam = getParam('group') ?? '';
  const hasMfaRaw = getParam('has_mfa');
  const hasMfa: 'all' | 'true' | 'false' =
    hasMfaRaw === 'true' || hasMfaRaw === 'false' ? hasMfaRaw : 'all';

  const groupsQuery = useQuery({
    queryKey: ['consoleGroups', 'all'],
    queryFn: () => groupsApi.list(),
    enabled: kind === 'system',
    staleTime: 5 * 60_000,
  });
  const groupOptions = groupsQuery.data?.results ?? [];

  // The tenant the grid is scoped to (embedded Staff tab fixes it; otherwise the
  // toolbar filter when it names exactly one operator — several act like none).
  // Drives the promote dialog's cross-tenant detection and the batch tenant.
  const gridTenantId = fixedTenantId ?? singleTenantId(tenants.map((t) => t.id));

  // Batch actions (async framework: ConsoleBatchMixin on the backend). The grid's
  // "Batch actions" menu shows only the ops the operator is permitted to run and
  // dispatches a background task. `promote` opens a custom dialog (pick an access
  // group); the rest are confirm-then-dispatch. Anonymize + delete are
  // destructive; anonymize additionally requires a reason (irreversible).
  const batchActions = useMemo<BatchActionDef[]>(() => {
    const actions: BatchActionDef[] = [
      {
        key: 'activate',
        label: 'Set active',
        icon: <ToggleOnIcon fontSize="small" />,
        permission: 'console_user_manage',
        action: 'activate',
        confirmText: (n) => `Activate ${n.toLocaleString()} user(s)? This runs in the background.`,
      },
      {
        key: 'deactivate',
        label: 'Set inactive',
        icon: <ToggleOffIcon fontSize="small" />,
        permission: 'console_user_manage',
        action: 'deactivate',
        confirmText: (n) =>
          `Deactivate ${n.toLocaleString()} user(s)? This runs in the background.`,
      },
      {
        key: 'reset_password',
        label: 'Reset password',
        icon: <LockResetIcon fontSize="small" />,
        permission: 'console_user_password',
        action: 'reset_password',
        confirmText: (n) =>
          `Send a password-reset link to ${n.toLocaleString()} user(s)? Guests are skipped.`,
      },
      {
        key: 'reset_mfa',
        label: 'Reset MFA',
        icon: <SecurityIcon fontSize="small" />,
        permission: 'console_user_mfa_manage',
        action: 'reset_mfa',
        destructive: true,
        confirmText: (n) =>
          `Reset MFA for ${n.toLocaleString()} user(s)? Their devices and recovery codes are removed and their ${MFA_RESET_SESSIONS[kind]} revoked.`,
      },
      {
        key: 'promote',
        label: 'Promote to staff',
        icon: <ShieldIcon fontSize="small" />,
        permission: 'console_perm_grant',
        action: 'promote',
        dialog: (ctx) => <PromoteToStaffDialog ctx={ctx} gridTenantId={gridTenantId} />,
      },
      {
        key: 'depromote',
        label: 'Depromote from staff',
        icon: <PersonOffIcon fontSize="small" />,
        permission: 'console_perm_grant',
        action: 'depromote',
        destructive: true,
        confirmText: (n) =>
          `Revoke staff from ${n.toLocaleString()} user(s)? Their access-group memberships and tour-operator links are removed.`,
      },
    ];
    if (kind === 'system') {
      actions.push({
        key: 'resend_invite',
        label: 'Resend invite',
        icon: <MailOutlineIcon fontSize="small" />,
        permission: 'console_user_invite',
        action: 'resend_invite',
        confirmText: (n) => `Re-send invites to ${n.toLocaleString()} user(s)?`,
      });
    }
    actions.push(
      entityExportBatchAction({
        permission: 'console_users_export',
        entity: 'user',
        resource: 'users',
        noun: 'user(s)',
      }),
    );
    actions.push({
      key: 'anonymize',
      label: 'Anonymize',
      icon: <VisibilityOffIcon fontSize="small" />,
      permission: 'console_user_manage',
      action: 'anonymize',
      destructive: true,
      reason: 'required',
      confirmText: (n) =>
        `Anonymize ${n.toLocaleString()} user(s)? This scrubs personal data and CANNOT be undone.`,
    });
    actions.push({
      ...deleteBatchAction('console_user_manage'),
      label: 'Delete',
      icon: <DeleteForeverIcon fontSize="small" />,
    });
    return actions;
  }, [kind, gridTenantId]);

  const columns = useMemo<GridColDef<ConsoleUserListItem>[]>(() => {
    const cols: GridColDef<ConsoleUserListItem>[] = [
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.5,
        filterOperators: containsOnly,
        renderCell: (p) => (
          <RouterLink
            to={`/users/${p.row.id}`}
            style={{ color: '#0a1a3f', textDecoration: 'none' }}
          >
            {p.row.email}
          </RouterLink>
        ),
      },
      {
        field: 'first_name',
        headerName: 'First name',
        flex: 0.8,
        filterOperators: containsOnly,
        renderCell: (p) => p.row.first_name || '—',
      },
      {
        field: 'last_name',
        headerName: 'Last name',
        flex: 0.8,
        filterOperators: containsOnly,
        renderCell: (p) => p.row.last_name || '—',
      },
    ];

    // `login` is in the backend ordering map (like email/name) → sortable.
    cols.push({
      field: 'login',
      headerName: 'Login',
      flex: 0.8,
      filterable: false,
      renderCell: (p) => p.row.login || '—',
    });
    cols.push({
      field: 'email_verified',
      headerName: 'Email verified',
      width: 130,
      // Not in the backend ordering map — display-only.
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        p.row.email_verified ? (
          <Chip size="small" color="success" label="Yes" />
        ) : (
          <Chip size="small" label="No" />
        ),
    });

    if (showTenant) {
      cols.push({
        field: 'tour_operator',
        headerName: 'Tenant',
        flex: 1,
        sortable: true,
        filterable: false,
        renderCell: (p) => {
          const top = p.row.tour_operator;
          if (!top) return '—';
          return (
            <WithTenantMark environment={top.environment}>
              <RouterLink to={`/tour-operators/${top.id}`} style={{ color: '#0a1a3f' }}>
                {top.code ? `${top.name} (${top.code})` : top.name}
              </RouterLink>
            </WithTenantMark>
          );
        },
      });
    }

    if (kind !== 'system') {
      cols.push({
        field: 'phone',
        headerName: 'Phone',
        width: 150,
        filterOperators: containsOnly,
        renderCell: (p) => p.row.phone || '—',
      });
      cols.push({
        field: 'phone_verified',
        headerName: 'Phone verified',
        width: 130,
        // Not in the backend ordering map — display-only.
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.phone_verified ? (
            <Chip size="small" color="success" label="Yes" />
          ) : (
            <Chip size="small" label="No" />
          ),
      });
    }

    cols.push({
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      sortable: kind === 'system',
      filterable: false,
      renderCell: (p) =>
        p.row.is_deleted ? (
          <Chip size="small" color="error" label="Deleted" />
        ) : p.row.is_active ? (
          <Chip size="small" color="success" label="Active" />
        ) : (
          <Chip size="small" label="Inactive" />
        ),
    });

    // `is_employee` (tour-operator staff flag) — not in the backend ordering map.
    cols.push({
      field: 'is_employee',
      headerName: 'Employee',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (p) => (p.row.is_employee ? <Chip size="small" color="primary" label="Yes" /> : '—'),
    });

    if (kind !== 'guest') {
      cols.push({
        field: 'mfa_enrolled',
        headerName: 'MFA',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.mfa_enrolled ? (
            <Chip size="small" color="primary" label="Enrolled" />
          ) : (
            <Chip size="small" color="warning" label="None" />
          ),
      });
    }

    cols.push({
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      minWidth: 160,
      sortable: false,
      filterable: false,
      // Account-level tags (CP-8095): facts about the PERSON, not about one of
      // their bookings. Clicking a chip filters the grid via the `tag` param.
      renderCell: (p) => (
        <TagBadgeSet tags={p.row.tags} onToggleTag={onToggleTag} isTagActive={isTagActive} />
      ),
    });

    if (kind === 'system') {
      cols.push({
        field: 'groups',
        headerName: 'Groups',
        flex: 1.2,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            {p.row.groups.map((g) => (
              <Chip key={g.id} label={g.name} size="small" variant="outlined" />
            ))}
          </Stack>
        ),
      });
      cols.push({
        field: 'pending_invite_count',
        headerName: 'Invite',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          (p.row.pending_invite_count ?? 0) > 0 ? (
            <Chip size="small" color="info" label="Pending" />
          ) : null,
      });
    }

    cols.push({
      field: 'active_tokens_count',
      headerName: 'Sessions',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        p.row.active_tokens_count > 0 ? (
          <RouterLink to={`/users/sessions?user=${p.row.id}`} style={{ color: '#0a1a3f' }}>
            {p.row.active_tokens_count}
          </RouterLink>
        ) : (
          '0'
        ),
    });
    cols.push({
      field: 'last_login',
      headerName: 'Last login',
      width: 140,
      sortable: kind === 'system',
      filterable: false,
      renderCell: (p) => formatRelativeTime(p.row.last_login),
    });
    // date_joined is in the backend ordering map; gated to system like last_login
    // (unindexed timestamp scan is costly on the 5M-row staff/guest tables).
    cols.push({
      field: 'date_joined',
      headerName: 'Joined',
      width: 140,
      sortable: kind === 'system',
      filterable: false,
      renderCell: (p) => formatRelativeTime(p.row.date_joined),
    });
    return cols;
  }, [kind, showTenant, onToggleTag, isTagActive]);

  // `extra` feeds BOTH the grid's query identity and the batch `filterQuery`;
  // the kind (and the Staff tab's fixed tenant) must ride along so an
  // all-matching batch/export resolves the same kind-scoped set the grid
  // shows. `listParams` re-states both after the spread with identical values,
  // so the list request itself is unchanged.
  const gridExtra = useMemo(
    () => ({
      ...extra,
      kind,
      ...(fixedTenantId !== undefined ? { tour_operator: fixedTenantId } : {}),
    }),
    [extra, kind, fixedTenantId],
  );

  // params.ordering only carries column-backed sorts; presets.ordering keeps
  // non-column orderings (e.g. -date_joined) and the kind default alive.
  const orderingFallback = presets.ordering;
  const listParams = useCallback(
    (params: ResourceDataGridParams) => ({
      kind,
      ordering:
        params.ordering ?? orderingFallback ?? (kind === 'system' ? '-date_joined' : '-id'),
      search: params.search,
      // All filters (header row + toolbar) arrive resolved in params.extra.
      ...(params.extra as Record<string, never>),
      // Embedded (Staff tab): the tenant is fixed and wins over any param.
      ...(fixedTenantId !== undefined ? { tour_operator: fixedTenantId } : {}),
    }),
    [kind, fixedTenantId, orderingFallback],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      usersApi.list({
        ...listParams(params),
        page: params.page,
        pageSize: params.pageSize,
        // Only set when the user overrode the grid's mode; omitting it lets the
        // backend's own default (deferred for guests) stand.
        ...(params.countMode ? { count_mode: params.countMode } : {}),
      }),
    [listParams],
  );

  // Same filters, count only — the pager's "Show total" click.
  const countOnly = useCallback(
    (params: ResourceDataGridParams) => usersApi.count(listParams(params)),
    [listParams],
  );

  const copy = KIND_COPY[kind];

  const toolbarItems: FilterBarItem[] = [
    ...(showTenant
      ? [
          {
            key: 'tenant',
            priority: 3,
            active: tenants.length > 0,
            node: (
              <Box sx={{ minWidth: 280 }}>
                <TenantPicker
                  multiple
                  value={tenants}
                  onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
                />
              </Box>
            ),
          } as FilterBarItem,
        ]
      : []),
    {
      key: 'status',
      priority: 2,
      active: status !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) =>
            setParam(
              'is_active',
              e.target.value === 'active' ? 'true' : e.target.value === 'inactive' ? 'false' : null,
            )
          }
          sx={{ width: 130 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      ),
    },
    ...(kind !== 'guest'
      ? [
          {
            key: 'is_staff',
            priority: 2,
            active: isStaff !== 'all',
            node: (
              <TextField
                select
                size="small"
                label="Staff"
                value={isStaff}
                onChange={(e) => setParam('is_staff', e.target.value === 'all' ? null : e.target.value)}
                sx={{ width: 130 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Staff</MenuItem>
                <MenuItem value="false">Non-staff</MenuItem>
              </TextField>
            ),
          } as FilterBarItem,
        ]
      : []),
    ...(kind === 'system'
      ? [
          {
            key: 'group',
            priority: 4,
            active: groupParam !== '',
            node: (
              <TextField
                select
                size="small"
                label="Group"
                value={groupParam}
                onChange={(e) => setParam('group', e.target.value || null)}
                sx={{ width: 180 }}
              >
                <MenuItem value="">All groups</MenuItem>
                {groupOptions.map((g) => (
                  <MenuItem key={g.id} value={String(g.id)}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
            ),
          } as FilterBarItem,
        ]
      : []),
    ...(kind === 'system' || (kind === 'staff' && embedded)
      ? [
          {
            key: 'has_mfa',
            priority: 3,
            active: hasMfa !== 'all',
            node: (
              <TextField
                select
                size="small"
                label="MFA"
                value={hasMfa}
                onChange={(e) => setParam('has_mfa', e.target.value === 'all' ? null : e.target.value)}
                sx={{ width: 150 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Enrolled</MenuItem>
                <MenuItem value="false">Not enrolled</MenuItem>
              </TextField>
            ),
          } as FilterBarItem,
        ]
      : []),
    {
      key: 'deleted',
      priority: 1,
      active: includeDeleted,
      node: (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeDeleted}
              onChange={(e) => setParam('deleted', e.target.checked ? 'true' : null)}
            />
          }
          label="Include deleted"
        />
      ),
    },
  ];

  return (
    <Box sx={embedded ? undefined : PAGE_FILL_SX}>
      {!embedded && (
        <PageHeader
          helpStory="users-three-kinds"
          title={copy.title}
          subtitle={copy.subtitle}
          actions={
            kind === 'system' &&
            hasPerm('console_user_invite') && (
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteOpen(true)}
              >
                Invite user
              </Button>
            )
          }
        />
      )}

      {/* Text filters (email / name / phone) live in the always-on header row;
          the toolbar carries the curated selects. Batch actions live in the
          grid's own "Batch actions" toolbar menu (shown once rows are ticked). */}
      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<ConsoleUserListItem>
          queryKey={['consoleUsers', kind, fixedTenantId ?? null]}
          queryFn={queryFn}
          countMode={{ grid: `users_${kind}`, countOnly }}
          columns={orderColumns(columns, presets.orderedFields)}
          layout={embedded ? 'auto' : 'fill'}
          {...(embedded ? { defaultPageSize: 25 } : {})}
          extra={gridExtra}
          batchResource="users"
          // Declared unconditionally (batch-gating lesson, 2026-07-24): each
          // action carries its own `permission`; the menu filters, the
          // backend re-checks. A page-level gate would hide the checkbox
          // column from export-only operators.
          batchActions={batchActions}
          batchTenantFilterId={gridTenantId}
          batchRowTenantId={(row) => row.tour_operator?.id ?? null}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Email, name, phone or ID',
            exportFileName: `users-${kind}`,
            ...(embedded ? {} : { presets: presets.toolbar }),
          }}
        />
      )}

      {kind === 'system' && (
        <UserInviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
      )}
    </Box>
  );
}
